import { Box, Image, Text } from "@chakra-ui/react";
import { useState, useEffect, useRef } from "react";
import React from "react";
import heroImage from "../../assets/games.png";
import { keyframes } from "@emotion/react";

// Left to right animation
const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export default function HeroSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <Box
      ref={sectionRef}
      position="relative"
      w="100%"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      mx="auto"
      mt={14}
      overflow="visible"
      px={4}
    >
      {/* Left Glow */}
      <Box
        position="absolute"
        top="60%"
        left="40"
        transform="translate(-30%, -50%)"
        w={{ base: "200px", md: "600px" }}
        h={{ base: "200px", md: "600px" }}
        bg="radial-gradient(circle, #8B5CF6 0%, rgba(0,0,0,0) 70%)"
        filter="blur(80px)"
        zIndex={0}
      />

      {/* Main Image */}
      <Image
        src={heroImage}
        alt="Games Showcase"
        maxW={{ base: "100%", md: "85%", lg: "65%" }}
        objectFit="contain"
        zIndex={1}
        sx={{
          WebkitMaskImage:
            "radial-gradient(circle at center, rgba(0,0,0,1) 65%, rgba(0,0,0,0) 100%)",
          maskImage:
            "radial-gradient(circle at center, rgba(0,0,0,1) 65%, rgba(0,0,0,0) 100%)",
        }}
      />

      {/* Glass Text Top */}
      <GlassBadge
        top={{ base: "5%", md: "3%" }}
        right={{ base: "5%", md: "28%" }}
        text="For everyone: Simple to start, satisfying to master—solo or with friends."
        delay={0}
        isVisible={isVisible}
      />

      {/* Glass Text Middle */}
      <GlassBadge
        top={{ base: "40%", md: "33%" }}
        right={{ base: "2%", md: "16%" }}
        px={12}
        text="Coin-only play: Earn and use coins in-game; there's no cash-out."
        delay={0.4}
        isVisible={isVisible}
      />

      {/* Glass Text Bottom */}
      <GlassBadge
        bottom={{ base: "8%", md: "36%" }}
        right={{ base: "4%", md: "13%" }}
        text="Cricket, Ludo, Rummy, Poker, and 8 Ball Pool made for quick matches and big one more game energy. No wagering, no cash out, no real money only coins"
        delay={0.8}
        isVisible={isVisible}
      />

      <GlassBadge
        bottom={{ base: "4%", md: "13%" }}
        right={{ base: "4%", md: "28%" }}
        text="Epic Tournaments: Turn small stakes into massive wins—climb the leaderboard and 100x your coins."
        delay={1.2}
        isVisible={isVisible}
      />

    </Box>
  );
}

/* Glass Badge Component with Staggered Animation */
const GlassBadge = React.forwardRef(({ text, delay = 0, isVisible = false, ...props }, ref) => {
  return (
    <Box
      ref={ref}
      position="absolute"
      zIndex={2}
      maxW={{ base: "240px", md: "560px" }}
      px={6}
      py={2}
      borderRadius="2xl"
      bg="rgba(255,255,255,0.08)"
      backdropFilter="blur(18px)"
      border="1px solid rgba(255,255,255,0.18)"
      boxShadow="0 8px 32px rgba(0,0,0,0.4)"
      color="white"
      fontSize={{ base: "12px", md: "14px" }}
      lineHeight="1.4"
      animation={isVisible ? `${slideInFromLeft} 0.6s ease-out ${delay}s both` : "none"}
      sx={{
        visibility: isVisible ? "visible" : "hidden",
        opacity: 0,
        transform: "translateX(-50px)",
      }}
      {...props}
    >
      <Text opacity={0.9}>{text}</Text>
    </Box>
  );
});

GlassBadge.displayName = "GlassBadge";
